import { Button, Select, TextField } from '../forms';
import { useDraft } from '../recovery/useDraft';
import { DraftNotice } from '../recovery/DraftNotice';
import { holdingDraftValid, type HoldingDraft } from './draft-shapes';
import { useState } from 'react';
import { Banner, BlockStack, Card, InlineStack } from '@shopify/polaris';
import { useTransferRead, type TransferClient } from './useTransferClient';
import { ResourceChoice } from './ResourceChoice';
import type { Transfer, Holding } from './types';
import { transferIdentityPayload } from '../../../../packages/shared/transfers/identity-draft';
import { TransferIdentities } from './TransferIdentities';
function HoldingOutcome({
  client,
  transfer,
  holding,
}: {
  client: TransferClient;
  transfer: Transfer;
  holding: Holding;
}) {
  const draft = useDraft<HoldingDraft>(client.draftKey + 'transfer-holding:' + transfer.id + ':' + holding.id, String(transfer.revision), { action: '', quantity: '', reason: '', bin: null }, holdingDraftValid);
  const [action, setAction] = draft.field('action'), [quantity, setQuantity] = draft.field('quantity'), [reason, setReason] = draft.field('reason'), [bin, setBin] = draft.field('bin');
  const [review, setReview] = useState(false);
  const [error, setError] = useState('');
  const [identities, setIdentities] = draft.field('identities');
  const valid =
    action &&
    Number.isSafeInteger(Number(quantity)) &&
    Number(quantity) > 0 &&
    Number(quantity) <= holding.remaining &&
    reason.trim().length >= 3 &&
    (action !== 'restock' || !!bin);
  const locked = client.locked || review || draft.locked;
  return (
    <details data-form-scope="">
      <summary>
        {holding.sku || holding.title}: {holding.remaining} held in {holding.holding_bin}
      </summary>
      <BlockStack gap="300">
        <p>These goods are held outside usable stock.</p>
        <DraftNotice draft={draft} locked={client.locked} />
        <Select
          label="Damaged goods outcome"
          requiredIndicator
          value={action}
          onChange={setAction}
          disabled={locked}
          options={[
            { label: 'Choose an outcome', value: '' },
            { label: 'Repaired and returned to usable stock', value: 'restock' },
            { label: 'Disposed after inspection', value: 'dispose' },
          ]}
        />
        <TextField
          label="Outcome quantity"
          type="number"
          min={1}
          max={holding.remaining}
          step={1}
          requiredIndicator value={quantity}
          onChange={setQuantity}
          disabled={locked}
          autoComplete="off"
        />
        {action === 'restock' && (
          <ResourceChoice
            client={client}
            label="Repaired goods bin"
            binKind={holding.untracked ? undefined : 'stock'}
            required
            warehouse={transfer.to_warehouse_id}
            value={bin}
            onChange={setBin}
            disabled={locked}
          />
        )}
        <TextField
          label="Outcome reason"
          requiredIndicator value={reason}
          onChange={setReason}
          disabled={locked}
          maxLength={500}
          autoComplete="off"
        />
        {!holding.untracked && action && <TransferIdentities client={client} transfer={transfer.id} item={holding.item_id} revision={transfer.revision} receipt={holding.id} action={action} value={identities} onChange={setIdentities} disabled={locked}/>}
        {error && <Banner tone="critical">{error}</Banner>}
        {review ? (
          <>
            <p>
              Confirm {quantity} {holding.sku || holding.title}:{' '}
              {action === 'restock' ? `add to usable stock in ${bin?.label}` : 'dispose without adding usable stock'}.{' '}
              {reason}
            </p>
            <InlineStack gap="200">
              <Button validate
                variant="primary"
                disabled={client.locked || draft.locked || !!draft.error || !valid}
                onClick={() =>
                  void client.submit(`/${transfer.id}/${action}`, {
                    revision: transfer.revision,
                    receipt_id: holding.id,
                    quantity: Number(quantity),
                    reason,
                    location_id: bin?.id || null,
                    ...(!holding.untracked ? transferIdentityPayload(identities, action, Number(quantity)) : {}),
                  }, draft.reference()).then(saved => { if (saved) draft.clear(); })
                }
              >
                Confirm damage outcome
              </Button>
              <Button disabled={client.locked || draft.locked || !!draft.error} onClick={() => setReview(false)}>
                Change outcome
              </Button>
            </InlineStack>
          </>
        ) : (
          <Button disabled={client.locked || draft.locked || !!draft.error || !valid} onClick={() => {
            try { if (!holding.untracked) transferIdentityPayload(identities, action, Number(quantity)); setError(''); setReview(true); } catch (e) { setError((e as Error).message); }
          }}>
            Review damage outcome
          </Button>
        )}
      </BlockStack>
    </details>
  );
}
export function TransferHoldings({
  client,
  transfer,
  refresh,
  canEdit,
}: {
  client: TransferClient;
  transfer: Transfer;
  refresh: number;
  canEdit: boolean;
}) {
  const [cursor, setCursor] = useState<number | null>(null);
  const { data, error } = useTransferRead<{ holdings: Holding[]; nextCursor: number | null }>(
    client,
    `/${transfer.id}/holdings${cursor ? '?cursor=' + cursor : ''}`,
    refresh,
  );
  return (
    <Card>
      <BlockStack gap="300">
        <h2>Damaged goods held at destination</h2>
        <p>{transfer.held} units held across the whole transfer.</p>
        {error && <Banner tone="critical">{error}</Banner>}
        {!data && !error && <p>Loading held goods…</p>}
        {data?.holdings.map((holding) =>
          canEdit ? (
            <HoldingOutcome
              key={`${holding.id}:${transfer.revision}`}
              client={client}
              transfer={transfer}
              holding={holding}
            />
          ) : (
            <p key={holding.id}>
              {holding.sku || holding.title}: {holding.remaining} in {holding.holding_bin}
            </p>
          ),
        )}
        {data && !data.holdings.length && <p>No held goods on this page.</p>}
        <InlineStack gap="200">
          <Button disabled={client.locked || !cursor} onClick={() => setCursor(null)}>
            First held page
          </Button>
          <Button disabled={client.locked || !data?.nextCursor} onClick={() => setCursor(data?.nextCursor || null)}>
            More held goods
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
